import CustomStore from "devextreme/data/custom_store";
import jwtService from "@service/jwt-service";
import { getHCostFactorList } from "@service/hcost/hcost-factor-service";
import { HCostConst } from "@constants/hcost/hcost-const";
import { CommonConst } from "@constants/common-const";
import { CommonUtil } from "@utils/CommonUtil";
import { HCOST_MODEL_FACTOR_DATA } from "@constants/hcost/hcost-data";
import { cloneDeep } from "lodash-es";
import { commonNotify } from "@utils/common";

const axios = jwtService.axiosIns;

// 간편견적 모델 목록 조회
async function getHCostModels(params: string) {
  return await axios.get(`/hcosts/models?${params}`);
}

// 간편견적 모델 목록 순서변경
async function reorderHCostModels(...args: any[]) {
  return await axios.put("/hcosts/models/reorder", ...args);
}

// 간편견적 모델 전체 목록 조회 - 간편견적 결과 등록 화면
async function _getHCostModelList() {
  return await axios.get(`/hcosts/models/all`);
}

// 간편견적 모델 전체 목록 조회 - 수익성 모델 등록 화면
async function getHCostModelListForStartup() {
  return await axios.get(`/hcosts/models/fbn`);
}

// 간편견적 모델 등록
async function createHCostModel(...args: any[]) {
  return await axios.post("/hcosts/models", ...args);
}

// 간편견적 모델 조희
export async function _getHCostModel(modelKey: string) {
  return await axios.get(`/hcosts/models/${modelKey}`);
}

// 간편견적 모델 수정
async function updateHCostModel(modelKey: string, ...args: any[]) {
  return await axios.put(`/hcosts/models/${modelKey}`, ...args);
}

/**
 * 간편견적 모델 저장하기
 */
export async function saveHCostModel(modifyType: string, hcostModel: HcostModel, hcostFactors: HcostFactor[]) {
  const modelFactors = createSelectedModelFactors(hcostModel, hcostFactors);

  const data: HcostModel = {
    ...hcostModel,
    modelFactors: modelFactors,
  };
  // 통신
  try {
    if (modifyType === CommonConst.MODIFY_TYPE_CREATE) {
      await createHCostModel(data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (modifyType === CommonConst.MODIFY_TYPE_UPDATE) {
      await updateHCostModel(data.modelKey, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
    commonNotify(CommonConst.completeSave);
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
  }
}

/**
 * 간편견적 모델 목록 가져오기(DxDataGrid용)
 */
export function getHCostModelDataSource() {
  return new CustomStore({
    key: "id",
    onLoading() {},
    onLoaded() {},
    load: async loadOptions => {
      // 파라미터 생성
      const params = CommonUtil.makeDxDataGridParams(loadOptions);
      try {
        const { data } = await getHCostModels(params);
        return data;
      } catch (e) {
        throw new Error("Data Loading Error");
      }
    },
  });
}

/**
 * 간편견적 모델 목록 가져오기(간편견적 등록)
 */
export async function getHCostModelList(modelType: string) {
  let data: any = null;

  try {
    // 통신
    if (modelType === HCostConst.modelUse.FBN) {
      data = await getHCostModelListForStartup();
    } else if (modelType === HCostConst.modelUse.HCOST) {
      data = await _getHCostModelList();
    }
  } catch (e) {
    console.log(e);
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
  }
  return data?.data;
}

/**
 * 간편견적 모델 조회하기
 * @param modelKey
 * @param isFactor
 * @param setHcostModel
 * @param setHcostFactors
 */
export async function getHCostModel(modelKey: string, isFactor: boolean, setHcostModel: any, setHcostFactors: any) {
  try {
    // 조회
    const { data } = await _getHCostModel(modelKey);

    // 간편견적 요소 불러오기
    if (isFactor) {
      await getHCostFactorList(data, setHcostModel, setHcostFactors);
    }

    return data;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
  }
}

/**
 * 간편견적 모델 순서변경 하기
 */
export async function reorderHCostModel(hcostModelId: number, newOrderIndex: number) {
  const data: any = {
    id: hcostModelId,
    orderIndex: newOrderIndex,
  };

  // 통신
  try {
    await reorderHCostModels(data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    commonNotify(CommonConst.completeOrder);
  } catch (e) {
    commonNotify(CommonConst.errorOrder, "error");
    throw e;
  }
}

/**
 * 선택된 모델요소배열 만들기
 */
function createSelectedModelFactors(hcostModel: HcostModel, hcostFactor: HcostFactor[]) {
  let hcostModelFactors = [];
  let hcostModelFactor: HcostModelFactor = null;
  for (const factor of hcostFactor) {
    if (factor.selectedNode && factor.selectedScopeKey && factor.factorScopes) {
      hcostModelFactor = cloneDeep(HCOST_MODEL_FACTOR_DATA);
      hcostModelFactor.model = hcostModel;
      hcostModelFactor.factor = factor;
      hcostModelFactor.factorScope = factor.factorScopes.find(
        (element: any) => element.scopeKey === factor.selectedScopeKey,
      );
      hcostModelFactors.push(hcostModelFactor);
    }
  }

  return hcostModelFactors;
}

/**
 * 알고리즘: 면적 <- 동수, 폭, 길이
 */
export function algorithm_basicAreaPyeong(basicBay: number, basicWidth: number, basicLength: number): number {
  return Math.ceil((basicBay * basicWidth * basicLength) / 3.3058);
}
